import { AfterViewInit, Component, inject, Inject, Signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { BadgeOptions } from '@app/interfaces';
import { Employee } from '@app/models/employee/employee.model';
import { TaxForm } from '@app/models/payroll/tax-form.abstract.model';
import { BaseForm } from '@app/modules/forms/base.form';
import { TaxReturnService } from '@app/modules/payroll/services';
import { AuthService, NotifyService } from '@app/services';
import { of } from 'rxjs';

@Component({
    template: ``,
})
export class ReturnUpdateView implements AfterViewInit {
    /**
     * The child forms both have an `isLoading` property which allows us to know if the form "Save" button is available or disabled
     */
    @ViewChild('form') form?: BaseForm & { isLoading: boolean };
    /**
     * The T4 or RL-1 slip
     */
    taxForm: TaxForm = this.route.snapshot.data.models.get(this.model);
    /**
     * Whether the user is editing the T4/RL-1 boxes or the associated employee. These will display two different views
     */
    subset: 'DETAILS' | 'EMPLOYEE' = this.route.snapshot.routeConfig?.path === 'edit-details' ? 'DETAILS' : 'EMPLOYEE';
    headerBadgeOptions: BadgeOptions = this.taxForm.stateBadge;

    get isLoading(): boolean {
        return this._isLoading || (this.form ? this.form.isLoading : true); // The form should never be undefined, therefore we default to loading=true in the ternary
    }
    set isLoading(isLoading: boolean) {
        this._isLoading = isLoading;
    }

    get hasErrors(): boolean {
        return Object.keys(this.taxForm.errors).length > 0;
    }

    // Since this class is meant to be extended, we inject all of the common services to avoid having to re-define them in the super classes
    protected notify: NotifyService = inject(NotifyService);
    protected auth: AuthService = inject(AuthService);
    protected router: Router = inject(Router);
    protected taxReturnService: TaxReturnService = inject(TaxReturnService);

    /**
     * The employee associated with the return slip
     */
    protected employee: Signal<Employee | undefined> = toSignal(
        this.subset === 'EMPLOYEE' ? this.taxReturnService.getEmployee(this.taxForm) : of(undefined)
    );

    private _isLoading = false;

    constructor(
        protected route: ActivatedRoute,
        @Inject('model') private model: 't4' | 'rl1' // Provided by the super class. This indicates which model to try and pull from the route via the resolver
    ) {}
    ngAfterViewInit(): void {
        // If there are errors from the backend, "submit" the form on the next tick.
        // This will cause these errors to show up in the form
        if (this.hasErrors && this.subset === 'DETAILS') {
            this.form?.submit();
        }
    }

    protected get isFormValid(): boolean {
        if (!this.form) {
            return false;
        }
        this.form.submit();

        return this.form.valid;
    }

    protected cancel(event: MouseEvent): void {
        event.preventDefault();
        this.router.navigate(['..'], { relativeTo: this.route });
    }
}
